import { ofType } from 'redux-observable';
import { filter, map, mapTo, switchMap, take } from 'rxjs/operators';
import { of, merge, timer } from 'rxjs';
import { ajax } from 'rxjs/ajax';

import {
   ADD_MODULE,
   AWAIT_TUTORIAL,
   exerciseChallengeReady,
   exerciseReady,
   INIT,
   PREPARE_NEXT_CHALLENGE,
} from '../actions';
import { randomNumberInRange, randomNumbersInRange } from '../interval/util';
import { assetsUrl } from '../../util/constants';
import { combineExerciseEpics } from '../combineExerciseEpics';
import { getRandomArrayElement } from '../../util/getRandomArrayElement';
import {
   setBeatCount,
   setInstruments,
   setPartsPerBeat,
   setSwings,
   setTempos,
   setView,
} from '../../interfaces/sequencer/actions';
import {
   sequencerInstrumentControlTypes,
   sequencerViewTypes,
} from '../../interfaces/sequencer/constants';
import { getCurrentLevel } from '../getCurrentLevel';
import { loadSoundKit, setSoundKit, SET_SOUND_KIT } from '../../shared/actions';
import { soundKitTypes } from '../../shared/constants';
import { checkLoadFromModule } from '../checkLoadFromModule';

const createView = (patternSpec) => {
   if (patternSpec.viewConfig?.viewType === sequencerViewTypes.pianoRoll) {
      return {
         ...patternSpec.viewConfig,
         noteRange:
            patternSpec.sequence[patternSpec.viewConfig.instrumentIndex]
               .noteRange,
      };
   }

   return {
      viewType: sequencerViewTypes.stepSequencer,
   };
};

const createTempos = (patternSpec) => {
   const randomTempo = randomNumberInRange(
      ...patternSpec.tempoRange,
      patternSpec.tempoIncrement
   );

   const initialTempoConfig = {
      range: patternSpec.tempoControlRange ||
         patternSpec.tempoRange || [85, 165],
      increment: patternSpec.tempoIncrement || 5,
      display: patternSpec.testTempo,
   };

   if (patternSpec.testTempo) {
      return {
         tempo: patternSpec.defaultTempo,
         referenceTempo: randomTempo,
         tempoConfig: initialTempoConfig,
      };
   }

   return {
      tempo: randomTempo,
      referenceTempo: randomTempo,
      tempoConfig: initialTempoConfig,
   };
};

const createSwings = (patternSpec) => {
   let randomSwing = 0;

   if (patternSpec.swingRange) {
      const incrementCount = patternSpec.swingIncrementCount || 100;

      const possibleValues = [];
      for (let i = 0; i <= incrementCount; i += 1) {
         possibleValues.push(i / incrementCount);
      }
      randomSwing = getRandomArrayElement(
         possibleValues.filter(
            (value) =>
               value >= patternSpec.swingRange[0] &&
               value <= patternSpec.swingRange[1]
         )
      );
   }

   const initialSwingConfig = {
      incrementCount: patternSpec.swingIncrementCount,
      display: patternSpec.testSwing,
   };

   if (patternSpec.testSwing) {
      return {
         swing: 0,
         referenceSwing: randomSwing,
         swingConfig: initialSwingConfig,
      };
   }

   return {
      swing: randomSwing,
      referenceSwing: randomSwing,
      swingConfig: initialSwingConfig,
   };
};

const createPattern = (patternSpec) => {
   const instruments = patternSpec.sequence.map((instrument) => {
      const groups = {};

      const mappedInstrument = {
         name: instrument.name,
         noteRange: instrument.noteRange,
         source: instrument.instrumentRole,
         sustainAll: instrument.sustainAll,
         forcedNote: instrument.forcedNote,
         forcedStartPart: instrument.forcedStartPart,
         test:
            patternSpec.testSequence !== false &&
            instrument.testPattern !== false,
         referencePattern: new Array(
            patternSpec.beatCount * patternSpec.partsParBeat
         )
            .fill(undefined)
            .map(() => []),
         controlType:
            instrument.controlType ||
            sequencerInstrumentControlTypes.stepSequencer,
      };

      let parts =
         instrument.parts || getRandomArrayElement(instrument.variations);

      if (!Array.isArray(parts)) {
         parts = Object.entries(parts).map(([i, value]) => ({
            startPart: i,
            ...value,
         }));
      }

      parts.forEach(({ startPart: i, ...value }) => {
         const index = Number(i);

         if (value === true) {
            mappedInstrument.referencePattern[index].push({
               note: 0,
               startPart: index,
            });
         }
         // maybe need to handle optional notes within array
         else if (Array.isArray(value)) {
            mappedInstrument.referencePattern[index] = value.map((note) => ({
               ...note,
               startPart: index,
            }));
         }
         // maybe need to handle more optinal cases (array of notes)
         else if (typeof value === 'object' && value?.optional === true) {
            if (Math.random() >= 0.5) {
               mappedInstrument.referencePattern[index].push({
                  note: 0,
                  ...value,
                  startPart: index,
               });
            }
         } else if (typeof value === 'object' && value?.group != null) {
            if (groups[value.group] == null) {
               groups[value.group] = [];
            }

            groups[value.group].push([index, value]);
         } else if (typeof value === 'object') {
            mappedInstrument.referencePattern[index].push({
               note: 0,
               ...value,
               startPart: index,
            });
         }
      });

      Object.entries(groups).forEach(([group, values]) => {
         const currentGroup = instrument.groupRules[group];

         if (currentGroup.optional && Math.random() < 0.5) {
            return;
         }

         if (currentGroup.nOf != null) {
            const indexes = randomNumbersInRange(currentGroup.nOf, [
               0,
               values.length - 1,
            ]);

            indexes.forEach((index) =>
               mappedInstrument.referencePattern[values[index][0]].push({
                  note: 0,
                  ...values[index][1],
                  startPart: values[index][0],
               })
            );
         }

         if (currentGroup.atLeastNOf != null) {
            const indexes = randomNumbersInRange(
               randomNumberInRange(currentGroup.atLeastNOf, values.length),
               [0, values.length - 1]
            );

            indexes.forEach((index) =>
               mappedInstrument.referencePattern[values[index][0]].push({
                  note: 0,
                  ...values[index][1],
                  startPart: values[index][0],
               })
            );
         }
      });

      return mappedInstrument;
   });

   if (patternSpec.modifiers) {
      Object.entries(patternSpec.modifiers).forEach(
         ([modifier, modifiedInstruments]) => {
            switch (modifier) {
               case 'oneOf': {
                  for (
                     let i = 0;
                     i < patternSpec.beatCount * patternSpec.partsParBeat;
                     i += 1
                  ) {
                     const instrumentsWithStep = modifiedInstruments.reduce(
                        (matched, currentInstrument) => {
                           if (
                              instruments.find(
                                 ({ source }) => source === currentInstrument
                              ).referencePattern[i].length > 0
                           ) {
                              return [...matched, currentInstrument];
                           }
                           return matched;
                        },
                        []
                     );

                     const toKeep = getRandomArrayElement(instrumentsWithStep);
                     instrumentsWithStep
                        .filter((instrument) => instrument !== toKeep)
                        .forEach((instrument) => {
                           instruments.find(
                              ({ source }) => source === instrument
                           ).referencePattern[i] = [];
                        });
                  }
                  break;
               }
               default: {
                  break;
               }
            }
         }
      );
   }

   instruments.forEach((instrument) => {
      if (instrument.test === false) {
         // needs to be done
         // eslint-disable-next-line no-param-reassign
         instrument.pattern = instrument.referencePattern;
      }
   });

   return instruments;
};

const configureInterfaceOnInitEpic = (action$) =>
   action$.pipe(
      ofType(INIT),
      switchMap(() =>
         merge(
            of(
               setInstruments([
                  {
                     source: 'Kick',
                     name: 'Kick',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
                  {
                     source: 'Snare',
                     name: 'Snare',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
                  {
                     source: 'Hat',
                     name: 'Hat',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
               ])
            )
         )
      )
   );

// Need to load soundkit on init if not load from module
const loadSoundkitOnInitEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT),
      filter(() => !checkLoadFromModule(state$.value)),
      map(() =>
         loadSoundKit({
            soundKitId: state$.value.activeExercise.exerciseDocument.soundKit,
         })
      )
   );

// Need to load soundkit on add module
const loadSoundKitOnAuxAddedEpic = (action$, state$) =>
   action$.pipe(
      ofType(ADD_MODULE),
      map((action) => action.moduleData.exerciseDocument.soundKit),
      filter(
         (soundKitId) =>
            state$.value.activeInterface.soundKits[soundKitId] == null
      ),
      map((soundKitId) => loadSoundKit({ soundKitId }))
   );

// Make sure soundkit is loaded before starting session
// Do this on await tutorial, because it means that module is refreshed and loaded
const prepareNextSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(AWAIT_TUTORIAL),
      switchMap(() => {
         const currentState = state$.value;
         const { activeExercise } = currentState;
         const moduleId = activeExercise.currentModuleId;

         const currentExerciseOrModule = moduleId
            ? activeExercise.modules[moduleId]
            : activeExercise;

         const level = getCurrentLevel(state$.value, moduleId);

         const currentSoundKitId =
            currentExerciseOrModule.exerciseDocument.levels[level - 1]
               .soundKit || currentExerciseOrModule.exerciseDocument.soundKit;

         let awaitKitTimerObservable;
         if (currentState.activeInterface.soundKit?.id === currentSoundKitId) {
            awaitKitTimerObservable = timer(0, 100);
         } else {
            awaitKitTimerObservable = action$.pipe(
               ofType(SET_SOUND_KIT),
               take(1),
               switchMap(() => timer(0, 100))
            );
         }

         return merge(
            of(
               setSoundKit({
                  type: soundKitTypes.soundKit,
                  id: currentSoundKitId,
               })
            ).pipe(
               filter(
                  () =>
                     currentState.activeInterface.soundKit?.id !==
                     currentSoundKitId
               )
            ),

            awaitKitTimerObservable.pipe(
               filter(() => {
                  const soundKit =
                     state$.value.activeInterface.soundKits[
                        state$.value.activeInterface.soundKit?.id
                     ];
                  return soundKit && !soundKit.loading;
               }),
               take(1),
               mapTo(exerciseReady())
            )
         );
      })
   );

// move to interface????
const loadSetSoundKitEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_SOUND_KIT),
      filter(
         (action) =>
            state$.value.activeInterface.soundKits[action.soundKit.id] == null
      ),
      map((action) => loadSoundKit({ soundKitId: action.soundKit.id }))
   );

const prepareNextChallengeEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_CHALLENGE),
      switchMap(() => {
         let moduleId = state$.value.activeExercise.currentModuleId;
         let exerciseId = state$.value.activeExercise.exerciseDocument.id;

         const level = getCurrentLevel(state$.value, moduleId);
         const exerciseDocument = moduleId
            ? state$.value.activeExercise.modules[moduleId].exerciseDocument
            : state$.value.activeExercise.exerciseDocument;

         const levelData = exerciseDocument.levels[level - 1];

         if (exerciseDocument.isModule) {
            exerciseId = exerciseDocument.mainExerciseId;
            moduleId = exerciseDocument.id;
         }

         if (levelData.patterns) {
            return of(getRandomArrayElement(levelData.patterns));
         }
         if (levelData.patternSources) {
            const mainFolder =
               level > exerciseDocument.maxUnpaidLevel ? 'secure' : 'public';

            const source = getRandomArrayElement(levelData.patternSources);
            const { path } = source;
            const patternId = randomNumberInRange(1, source.count);

            return ajax({
               url: `${assetsUrl}/${mainFolder}/exercise/${exerciseId}${
                  moduleId ? `/module/${moduleId}` : ''
               }/pattern/${path}/${patternId}.json`,
               headers: {
                  Authorization: `Bearer ${state$.value.appData.permissions.token}`,
               },
               // withCredentials: true,
               // crossDomain: true,
            }).pipe(map(({ response }) => response));
         }

         throw new Error('no pattern or patternSources');
      }),
      switchMap((patternSpec) =>
         merge(
            of(setPartsPerBeat(patternSpec.partsParBeat)),
            of(setBeatCount(patternSpec.beatCount)),
            of(setTempos(createTempos(patternSpec))),
            of(setSwings(createSwings(patternSpec))),
            of(setInstruments(createPattern(patternSpec))),
            of(setView(createView(patternSpec))),
            of(exerciseChallengeReady())
         )
      )
   );

export const transcriptionExerciseEpics = combineExerciseEpics(
   configureInterfaceOnInitEpic,
   loadSoundkitOnInitEpic,
   loadSoundKitOnAuxAddedEpic,
   loadSetSoundKitEpic,
   prepareNextSessionEpic,
   prepareNextChallengeEpic
);
